// @src/pre-deployment/src/components/LevelPage.js v1.9.14
// v1.9.14: Wire Settings button to open sidebar; configurable padding via --level-page-padding
// v1.9.13: Simplified to match AppHomepage layout; keyboard navigation

import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useChapterProgress } from '../../data/tracking';
import { useChapterLocation } from '../../data/useChapterLocation';
import useDataService from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';

import Sidebar from './Sidebar';
// Import CSS directly to ensure styles are applied
import './LevelPage.css';

// Infer media type from URL for video vs image background
function getMediaType(url) {
  const ext = (url || '').split('.').pop()?.toLowerCase();
  if (['mp4', 'webm'].includes(ext)) return 'video';
  return 'image';
}

// ============================================
// DYNAMIC BACKGROUNDS (10 Chapter Variants)
// ============================================
const CHAPTER_BACKGROUNDS = [
  'linear-gradient(135deg, #1a1a1a 0%, #0a0a0c 100%)', // Ch1 - Dark
  'linear-gradient(135deg, #1e1b2e 0%, #0a0a0c 100%)', // Ch2 - Midnight
  'linear-gradient(135deg, #161d2e 0%, #0a0a0c 100%)', // Ch3 - Oceanic
  'linear-gradient(135deg, #162e21 0%, #0a0a0c 100%)', // Ch4 - Forest
  'linear-gradient(135deg, #2e161d 0%, #0a0a0c 100%)', // Ch5 - Crimson
  'linear-gradient(135deg, #2e2616 0%, #0a0a0c 100%)', // Ch6 - Ember
  'linear-gradient(135deg, #2e1b16 0%, #0a0a0c 100%)', // Ch7 - Burnt
  'linear-gradient(135deg, #2e1629 0%, #0a0a0c 100%)', // Ch8 - Purple
  'linear-gradient(135deg, #162e2e 0%, #0a0a0c 100%)', // Ch9 - Slate
  'linear-gradient(135deg, #1b2e2e 0%, #0a0a0c 100%)', // Ch10 - Teal
];

// ============================================
// MAIN LEVEL PAGE COMPONENT
// ============================================
const LevelPage = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  // State for which chapter is selected
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inlineMessage, setInlineMessage] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Configurable grid padding – override by setting the CSS variable, e.g.:
  // document.documentElement.style.setProperty('--level-page-padding', '2.5rem');
  // Default: 1.5rem

  // Get chapter progress for unlocked status
  const { data: chapter1Data } = useChapterProgress(user?.uid, level, 1);
  const { data: chapter2Data } = useChapterProgress(user?.uid, level, 2);

  console.group('📚 PRE-DEPLOYMENT LEVEL PAGE: Component loaded');
  console.log('Level:', level);
  console.log('User:', user?.uid);
  console.log('Current chapter index:', currentIndex);
  console.groupEnd();

  // Define chapters with proper IDs and short names for timeline
  const allChapters = [
    { id: 0, title: 'Hangul Practice', shortName: 'Hangul', description: 'Master the Korean alphabet and keyboard layout' },
    { id: 1, title: 'Basic Greetings and Introductions', shortName: 'Greetings', description: 'Learn how to introduce yourself and greet others' },
    { id: 2, title: 'Numbers and Time', shortName: 'Numbers', description: 'Master Korean numbers and telling time' },
    { id: 3, title: 'Family and Relationships', shortName: 'Family', description: 'Vocabulary for family members and relationships' },
    { id: 4, title: 'Food and Dining', shortName: 'Food', description: 'Essential words for ordering food and dining out' },
    { id: 5, title: 'Transportation', shortName: 'Transport', description: 'Navigate Korean public transportation and travel' },
    { id: 6, title: 'Shopping and Money', shortName: 'Shopping', description: 'Learn to shop and handle money transactions' },
    { id: 7, title: 'Daily Activities', shortName: 'Daily', description: 'Describe your daily routine and activities' },
    { id: 8, title: 'Weather and Seasons', shortName: 'Weather', description: 'Talk about weather and seasonal activities' },
    { id: 9, title: 'Hobbies and Interests', shortName: 'Hobbies', description: 'Express your hobbies and personal interests' },
    { id: 10, title: 'Past Tense and Experiences', shortName: 'Past', description: 'Talk about past events and experiences' }
  ];

  // Filter chapters based on level
  const chapters = useMemo(() => {
    if (parseInt(level) === 0) {
      return [{ id: 1, title: 'Hangul Practice', shortName: 'Hangul', description: 'Master the Korean alphabet and keyboard layout', isHangul: true }];
    }
    return allChapters.filter(c => c.id > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  // Dynamic background based on current chapter
  const currentBackground = useMemo(() => {
    return CHAPTER_BACKGROUNDS[currentIndex] || CHAPTER_BACKGROUNDS[0];
  }, [currentIndex]);

  const currentChapter = useMemo(() => {
    return chapters[currentIndex] || chapters[0];
  }, [chapters, currentIndex]);

  // Pull chapter location + tracking data for stats
  const { data: chapterConfig } = useChapterLocation(level, currentChapter?.id || 1);
  const { data: trackingData } = useDataService.useChapterTracking(user?.uid, level, currentChapter?.id || 1);

  const chapterStats = useMemo(() => {
    if (!chapterConfig?.locations) {
      return {
        locationsCompleted: 0,
        locationsTotal: 0,
        modulesCompleted: 0,
        modulesTotal: 0
      };
    }

    const locations = chapterConfig.locations || [];
    const modulesByLocation = locations.map((loc) =>
      (loc.modules || []).map((mod) => (typeof mod === 'string' ? mod : mod.moduleId))
    );

    const allModuleKeys = modulesByLocation.flat();
    const modulesTotal = allModuleKeys.length;
    const modulesCompleted = allModuleKeys.filter((key) => trackingData?.modules?.[key]?.completed).length;

    const locationsTotal = locations.length;
    const locationsCompleted = locations.filter((loc, idx) => {
      const keys = modulesByLocation[idx];
      if (!keys.length) return false;
      return keys.every((key) => trackingData?.modules?.[key]?.completed);
    }).length;

    return { locationsCompleted, locationsTotal, modulesCompleted, modulesTotal };
  }, [chapterConfig, trackingData]);

  // Keyboard navigation (ArrowLeft/ArrowRight) - NO WRAPPING
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < chapters.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, chapters.length]);

  // Check if chapter is unlocked
  const isChapterUnlocked = (chapterNum) => {
    if (parseInt(level) === 0) return true;
    if (chapterNum === 1) return true;
    if (chapterNum === 2 && chapter1Data?.chapter_completed) return true;
    if (chapterNum === 3 && chapter2Data?.chapter_completed) return true;
    return false;
  };

  // Navigate carousel (click on side cards)
  const handleCarouselNav = (direction) => {
    if (direction === 'prev' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (direction === 'next' && currentIndex < chapters.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const getChapterDifficulty = (chapterNumber) => {
    const difficulties = ['beginner', 'beginner', 'beginner', 'intermediate', 'intermediate', 'intermediate', 'intermediate', 'advanced', 'advanced', 'advanced'];
    return difficulties[chapterNumber - 1] || 'beginner';
  };
  const getChapterTheme = (chapterNumber) => {
    const themes = ['daily_life', 'daily_life', 'family', 'food', 'transportation', 'shopping', 'daily_life', 'weather', 'hobbies', 'past_tense'];
    return themes[chapterNumber - 1] || 'daily_life';
  };
  const getChapterEstimatedTime = (chapterNumber) => {
    const times = [45, 50, 55, 60, 65, 70, 75, 80, 85, 90];
    return times[chapterNumber - 1] || 45;
  };

  const handleChapterAction = async (chapterNumber, action) => {
    console.log('📚 PRE-DEPLOYMENT LEVEL PAGE: Chapter action clicked');
    console.log('Chapter:', chapterNumber);
    console.log('Action:', action);
    console.log('Level:', level);

    // Check if chapter is unlocked
    if (!isChapterUnlocked(chapterNumber)) {
      setInlineMessage(`Chapter ${chapterNumber} is locked. Complete the previous chapter first.`);
      return;
    }

    // Handle learn action (same as start)
    if (action === 'learn') {
      // REDIRECT FOR LEVEL 0 HANGUL PRACTICE
      if (parseInt(level) === 0) {
        console.log('🎹 Starting Hangul Practice Module');
        navigate('/hangul-practice');
        return;
      }

      console.log('🚀 Initializing tracking documents for chapter start');

      // Try-catch tracking but ALWAYS navigate
      try {
        if (user?.uid) {
          await TrackingV2Service.initializeDocuments(user.uid, level, chapterNumber, {
            difficulty: getChapterDifficulty(chapterNumber),
            theme: getChapterTheme(chapterNumber),
            estimatedTime: getChapterEstimatedTime(chapterNumber),
            startMethod: 'user_initiated',
            startedAt: new Date().toISOString()
          });
          console.log('✅ Tracking documents initialized successfully');
        } else {
          console.log('⚠️ User not authenticated, skipping tracking initialization');
        }
      } catch (error) {
        console.error('❌ Failed to initialize tracking documents:', error);
      }

      // NAVIGATION MUST HAPPEN REGARDLESS
      console.log(`NAVIGATING TO: /chapter-page/${level}/${chapterNumber}`);
      navigate(`/chapter-page/${level}/${chapterNumber}`);
      return;
    }

    // Handle review action
    if (action === 'review') {
      console.log(`🔄 Navigating to Review for Level ${level} Chapter ${chapterNumber}`);
      navigate(`/level/${level}/chapter/${chapterNumber}/review`);
      return;
    }

    // For other actions, show a placeholder message
    setInlineMessage(`Pre-Deployment: ${action.charAt(0).toUpperCase() + action.slice(1)} - Chapter ${chapterNumber}. This feature will be available in the full version.`);
  };

  // RESTORED: Original image path pattern from v1.2.9 (BUG-009 fix)
  const getChapterImage = (chapterNumber) => {
    return `/img/level_${Math.min(Math.max(chapterNumber, 1), 6)}.jpg`;
  };

  const returnToMenu = () => {
    console.log('📚 PRE-DEPLOYMENT LEVEL PAGE: Returning to level selection');
    navigate('/app-homepage');
  };

  const levelNames = {
    0: '0급 - Complete Beginner',
    1: '1급 - Beginner',
    2: '2급 - Elementary',
    3: '3급 - Pre-Intermediate',
    4: '4급 - Intermediate',
    5: '5급 - Upper-Intermediate',
    6: '6급 - Advanced'
  };

  const userLevel = userProfile?.assessment?.finalLevel || 1;

  const currentChapterLabel = currentChapter
    ? `Ch.${currentChapter.id}`
    : 'Ch.1';

  const currentChapterTitle = currentChapter?.title || 'Select a Chapter';

  const currentMediaUrl = getChapterImage(currentChapter?.id || 1);
  const mediaType = getMediaType(currentMediaUrl);

  return (
    <div className="level-page-container" style={{ background: currentBackground }}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Inline message remains (locked / placeholder info) */}
      {inlineMessage && (
        <div className="module-info" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, fontSize: '13px', padding: '0.5rem 1rem', background: '#f8d7da', color: '#721c24', borderBottom: '1px solid #f5c6cb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <span>{inlineMessage}</span>
          <button type="button" onClick={() => setInlineMessage(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#721c24' }} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Background: chapter image/gif/video + gradient vignette */}
      <div className="chapter-background-layer">
        {mediaType === 'video' ? (
          <video
            key={currentMediaUrl}
            src={currentMediaUrl}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img src={currentMediaUrl} alt="" aria-hidden />
        )}
      </div>
      <div className="chapter-gradient-overlay" aria-hidden />

      {/* Main content layout */}
      <div className="level-main-content">
        {/* Brand / level strip */}
        <div className="level-brand">
          NUNCHI
        </div>

        {/* Chapter title + stats (top-right) */}
        <div className="chapter-title-box">
          <div className="chapter-level-label">
            {levelNames[level] || `Level ${level}`} • Your level {userLevel}급
          </div>
          <div className="chapter-main-title">
            {currentChapterLabel}
          </div>
          <div className="chapter-subtitle">
            {currentChapterTitle}
          </div>
          <div className="chapter-stats">
            {chapterStats.locationsCompleted}/{chapterStats.locationsTotal} locations ·{' '}
            {chapterStats.modulesCompleted}/{chapterStats.modulesTotal} modules
          </div>
        </div>

        {/* Left-side menu: Learn, Review, Map, Settings, Back */}
        <div className="chapter-menu-vertical">
          <button
            onClick={() => currentChapter && handleChapterAction(currentChapter.id, 'learn')}
          >
            <i className="fas fa-play"></i>
            <span>Learn</span>
          </button>
          <button
            onClick={() => currentChapter && handleChapterAction(currentChapter.id, 'review')}
          >
            <i className="fas fa-redo"></i>
            <span>Review</span>
          </button>
          <button onClick={() => navigate('/map')}>
            <i className="fas fa-map"></i>
            <span>Map</span>
          </button>
          <button onClick={() => setIsSidebarOpen(true)}>
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </button>
          <button onClick={returnToMenu}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
        </div>

        {/* Bottom-right: Enter + chapter navigation arrows */}
        <div className="chapter-nav-actions">
          <button
            className="chapter-enter-btn"
            onClick={() => currentChapter && handleChapterAction(currentChapter.id, 'learn')}
          >
            들어가기
          </button>
          <div className="chapter-nav-arrows">
            {currentIndex > 0 && (
              <button onClick={() => handleCarouselNav('prev')}>
                ← 이전
              </button>
            )}
            {currentIndex < chapters.length - 1 && (
              <button onClick={() => handleCarouselNav('next')}>
                다음 →
              </button>
            )}
          </div>
        </div>

        {/* Compact timeline at bottom (no background) */}
        <div className="chapter-timeline-mini">
          <span>
            Chapter{' '}
            <span className="chapter-timeline-mini-strong">
              {currentIndex + 1}
            </span>
            {' / '}
            {chapters.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LevelPage;
